import { apiClient, createAuthHeaders } from '../api-client';
import { firebaseAuth } from '../firebase';
import type {
  SkillsCatalogResponse,
  MarketplaceSkillFull,
  InstalledSkill,
  SkillsCategoryCount,
  CustomSkillItem,
  CreateCustomSkillPayload,
  UpdateCustomSkillPayload,
} from '@optidev/shared-types';

async function getToken(): Promise<string> {
  const user = firebaseAuth.currentUser;
  if (!user) throw new Error('Not authenticated');
  return user.getIdToken();
}

export const skillsMarketplaceApi = {
  async getCatalog(params?: {
    search?: string;
    category?: string;
    featured?: boolean;
    popular?: boolean;
    page?: number;
    limit?: number;
  }): Promise<SkillsCatalogResponse> {
    const token = await getToken();
    const response = await apiClient.get('/api/skills-marketplace/catalog', {
      headers: createAuthHeaders(token),
      params,
    });
    return response.data;
  },

  async getCategories(): Promise<SkillsCategoryCount[]> {
    const token = await getToken();
    const response = await apiClient.get(
      '/api/skills-marketplace/catalog/categories',
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  async getSkillDetail(id: string): Promise<MarketplaceSkillFull> {
    const token = await getToken();
    const response = await apiClient.get(
      `/api/skills-marketplace/catalog/${id}`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  async getInstalled(): Promise<InstalledSkill[]> {
    const token = await getToken();
    const response = await apiClient.get('/api/skills-marketplace/installed', {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  async install(skillId: string): Promise<{ success: boolean; installedAt: string }> {
    const token = await getToken();
    const response = await apiClient.post(
      '/api/skills-marketplace/install',
      { skillId },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  async uninstall(skillId: string): Promise<{ success: boolean }> {
    const token = await getToken();
    const response = await apiClient.post(
      '/api/skills-marketplace/uninstall',
      { skillId },
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  async getCustomSkills(): Promise<CustomSkillItem[]> {
    const token = await getToken();
    const response = await apiClient.get('/api/skills-marketplace/custom', {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  async getCustomSkill(id: string): Promise<CustomSkillItem> {
    const token = await getToken();
    const response = await apiClient.get(`/api/skills-marketplace/custom/${id}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  async createCustomSkill(dto: CreateCustomSkillPayload): Promise<CustomSkillItem> {
    const token = await getToken();
    const response = await apiClient.post('/api/skills-marketplace/custom', dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  async updateCustomSkill(id: string, dto: UpdateCustomSkillPayload): Promise<CustomSkillItem> {
    const token = await getToken();
    const response = await apiClient.patch(`/api/skills-marketplace/custom/${id}`, dto, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  async deleteCustomSkill(id: string): Promise<{ success: boolean }> {
    const token = await getToken();
    const response = await apiClient.delete(`/api/skills-marketplace/custom/${id}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },
};
